import { Assignment, CallReceived, HorizontalRule, SwapVert } from "@mui/icons-material"
import { useEffect, useMemo } from "react"
import intl from "src/internationalization/intl"
import { Accordion, Checkbox, Chip, Grid, IconWrapper, InnerSection, InvestorProfileChip, Typography } from "src/lib"
import { formatCurrencyBRL, mapPerfilToProfile } from "src/lib/transformers/ClientsOverviewInfo"
import { toPascalCase } from "src/lib/utils/stringUtils"
import { CLIENT_EVENT_ACCORDION_EXPAND_DISABLED } from "src/config/featureFlags"
import { generateRecommendationProducts, mergeRankingTexts } from "src/services/api/commitments"
import { useCashRecommendationStore } from "src/store/cashRecommendationStore"
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore"
import { useModalStore } from "src/store/modalStore"
import { CashOpportunityPayload, ISummaryProps } from "./types"

const DEFAULT_RANKINGS = {
    fund_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"FUNDOS\"})\nWHERE p.minimumInitialInvestment is not null and p.minimumInitialInvestment is::FLOAT\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    fixed_income_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"RENDA FIXA\"})\nWHERE p.dateDue is not null\nORDER BY p.dateDue DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    retirement_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"PREVIDENCIA\"})\nWHERE p.minimumInitialInvestment is not null and p.minimumInitialInvestment is::FLOAT\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    variable_income_products_query: "",
    crypto_products_query: "",
    coe_products_query: "",
}

const Summary = (props: ISummaryProps) => {
    const setOpen = useModalStore((s: any) => s.setOpen)
    const setLoading = useCashRecommendationStore((s) => s.setLoading)
    const setResult = useCashRecommendationStore((s) => s.setResult)
    const setError = useCashRecommendationStore((s) => s.setError)
    const confirmedRankingByMarket = useConfirmedRankingStore((s) => s.confirmedRankingByMarket)
    const confirmedFundos = useConfirmedRankingStore((s) => s.getConfirmedRanking("FUNDOS"))
    const confirmedPrevidencia = useConfirmedRankingStore((s) => s.getConfirmedRanking("PREVIDENCIA"))
    const confirmedRendaFixa = useConfirmedRankingStore((s) => s.getConfirmedRanking("RENDA FIXA"))
    const rankings = useMemo(() => {
        const base = {
            ...DEFAULT_RANKINGS,
            fund_products_query: confirmedFundos?.cypher_query ?? DEFAULT_RANKINGS.fund_products_query,
            fixed_income_products_query:
                confirmedRendaFixa?.cypher_query ?? DEFAULT_RANKINGS.fixed_income_products_query,
            retirement_products_query:
                confirmedPrevidencia?.cypher_query ?? DEFAULT_RANKINGS.retirement_products_query,
        }
        return mergeRankingTexts(base, confirmedRankingByMarket)
    }, [
        confirmedFundos?.cypher_query,
        confirmedRendaFixa?.cypher_query,
        confirmedPrevidencia?.cypher_query,
        confirmedRankingByMarket,
    ])
    const summaryType = props.summaryType ?? "commitment"
    const isOpportunity = summaryType === "opportunity"
    const checked = props.client.liquidityEventResolution === true
    const columnWidths = {
        client: 175,
        event: 175,
        motive: 175,
        liquidity: 175,
        equity: 160,
        profile: 160,
    }

    // Obtém o commitment_id (usando due_date do commitment)
    const getCommitmentId = () => {
        return props.client?.produto_a_vencer?.commitmentId || ""
    }

    const handleCheckboxClick = () => {
        const commitmentId = getCommitmentId()
        const clientName = props.client?.client?.name || ""
        const accountId = props.client?.client?.account
        
        // Validação: accountId não pode ser null, undefined ou string vazia (mas pode ser 0)
        if (accountId === null || accountId === undefined || accountId === "") {
            console.error("Erro: accountId do cliente é obrigatório e não pode ser null, undefined ou vazio")
            return
        }
        
        // Abre o modal de confirmação com os dados necessários
        setOpen("confirmCommitmentStatusModal", {
            commitmentId,
            currentStatus: checked,
            clientName,
            accountId,
            dueDate: props.client?.last_updated_date || "",
        })
    }

    const opportunities = props.opportunities ?? []
    const totalOpportunityValue = opportunities.reduce((acc: number, opportunity: any) => {
        return acc + (opportunity?.volumeOportunidade ?? 0)
    }, 0)

    useEffect(() => {
        if (CLIENT_EVENT_ACCORDION_EXPAND_DISABLED || !props.open) return
        const rawAccount = props.client?.client?.account
        const account = rawAccount != null && rawAccount !== "" ? String(rawAccount) : null
        if (account == null) return

        if (props.client?.is_cash_opportunity) {
            const payload: CashOpportunityPayload = {
                client: account,
                opportunity: "",
                event: { cliente_com_caixa: { net_worth: Number(props.client?.net_worth ?? 0) } },
                rankings,
            }
            setLoading(account, "cash")
            generateRecommendationProducts(payload)
                .then((response) => setResult(account, "cash", response))
                .catch((err: any) => setError(account, "cash", err?.message ?? "Erro ao carregar recomendações."))
            return
        }

        if (props.client?.produto_a_vencer) {
            const payload: CashOpportunityPayload = {
                client: account,
                opportunity: "",
                event: {
                    produto_a_vencer: {
                        commitmentId: props.client?.produto_a_vencer?.commitmentId ?? "",
                        nameProduct: props.client?.produto_a_vencer?.nameProduct ?? "",
                    },
                },
                rankings,
            }
            setLoading(account, "commitment")
            generateRecommendationProducts(payload)
                .then((response) => setResult(account, "commitment", response))
                .catch((err: any) => setError(account, "commitment", err?.message ?? "Erro ao carregar recomendações."))
        }
    }, [props.open, props.client?.is_cash_opportunity, props.client?.client?.account, props.client?.net_worth, props.client?.produto_a_vencer, isOpportunity, setLoading, setResult, setError])

    return (
        <Accordion.Summary
            open={props.open}
            onToggle={props.onToggle}
            toggleDisabled={CLIENT_EVENT_ACCORDION_EXPAND_DISABLED}
        >
            <Grid container justifyContent="space-between">
                <Grid sx={{ flexShrink: 0 }}>
                    <Grid container gap={3}>
                        <Grid sx={{ width: columnWidths.client, minWidth: columnWidths.client, flexShrink: 0 }}>
                            <Grid container gap={2} alignItems="start">
                                <Checkbox
                                    color="secondary"
                                    checked={checked}
                                    onClick={handleCheckboxClick}
                                />
                                <Grid>
                                    <Typography>
                                        {props.client.client.name}
                                    </Typography>
                                    {!isOpportunity && (!!props.client.recommendations || !!props.client.produto_a_vencer) &&
                                        <Grid container alignItems="center" sx={{ mt: 1 }}>
                                            {props.client.produto_a_vencer &&
                                                <IconWrapper color="quaternary">
                                                    <Assignment />
                                                </IconWrapper>
                                            }
                                            {props.client.recommendations &&
                                                <IconWrapper color="tertiary">
                                                    <SwapVert />
                                                </IconWrapper>
                                            }
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid sx={{ width: columnWidths.event, minWidth: columnWidths.event, flexShrink: 0 }}>
                            {isOpportunity ? (
                                <InnerSection title="Quantidade de oportunidades">
                                    <Typography variant="body1">
                                        {opportunities.length}
                                    </Typography>
                                </InnerSection>
                            ) : (
                                <InnerSection
                                title={toPascalCase(intl('event'))}
                                >
                                    <Grid container>
                                        <IconWrapper color={props.client.is_cash_opportunity ? "secondary" : "error"}>
                                            {props.client.is_cash_opportunity ? <HorizontalRule/> : <CallReceived/>}
                                        </IconWrapper>
                                        <Typography variant="body1">
                                            {formatCurrencyBRL(props.client.net_amount)}
                                        </Typography>
                                    </Grid>
                                    <Grid container alignItems="center">
                                        <Typography variant="caption">
                                            {`${toPascalCase(intl("reference"))}: ${props.client.finantialInstitution}`}
                                        </Typography>
                                        {/* <IconButton color="primary" variant="clean">
                                            <InfoOutlined/>
                                        </IconButton> */}
                                    </Grid>
                                </InnerSection>
                            )}
                        </Grid>
                        <Grid sx={{ width: columnWidths.motive, minWidth: columnWidths.motive, flexShrink: 0 }}>
                            {isOpportunity ? (
                                <InnerSection title="Valor total das oportunidades">
                                    <Typography variant="body1">
                                        {formatCurrencyBRL(totalOpportunityValue)}
                                    </Typography>
                                </InnerSection>
                            ) : (
                                <InnerSection
                                title={toPascalCase(intl('motive'))}
                                >
                                    <Typography variant="body1">
                                        {props.client.is_cash_opportunity ? "Conta corrente" : props.client.produto_a_vencer?.nameProduct}
                                    </Typography>
                                    <Grid container alignItems="center">
                                        <Typography variant="caption">
                                            {`${toPascalCase(intl("reference"))}: ${props.client.finantialInstitution}`}
                                        </Typography>
                                        {/* <IconButton color="primary" variant="clean">
                                            <InfoOutlined/>
                                        </IconButton> */}
                                    </Grid>
                                </InnerSection>
                            )}
                        </Grid>
                        <Grid sx={{ width: columnWidths.liquidity, minWidth: columnWidths.liquidity, flexShrink: 0 }}>
                            <InnerSection title={intl('liquidityStream')}>
                                <Grid>
                                    {isOpportunity ? (
                                        <Chip label="Oportunidade" sx={{ backgroundColor: "#BD6DB6", color: "#fff" }} />
                                    ) : (
                                        props.client.is_cash_opportunity ? <Chip color="warning" label="Caixa" /> : <Chip color="error" label={intl('maturity')} />
                                    )}
                                </Grid>
                            </InnerSection>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid sx={{ flex: 1 }} />

                <Grid sx={{ flexShrink: 0 }}>
                    <Grid container gap={2}>
                        <Grid sx={{ width: columnWidths.equity, minWidth: columnWidths.equity, flexShrink: 0 }}>
                            <InnerSection
                            title={toPascalCase(intl('equity'))}
                            >
                                <Typography variant="body1">
                                    {formatCurrencyBRL(props.client.net_worth)}
                                </Typography>
                            </InnerSection>
                        </Grid>
                        <Grid sx={{ width: columnWidths.profile, minWidth: columnWidths.profile, flexShrink: 0 }}>
                            <InnerSection
                            title={toPascalCase(intl('profile'))}
                            >
                                <Grid>
                                    <InvestorProfileChip profile={mapPerfilToProfile(props.client.client.perfil)} />
                                </Grid>
                            </InnerSection>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Accordion.Summary>
    )
}

export { Summary }
